import React from 'react';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {getByScreenSize} from '../../utils/responsive';
import {GenericButtonProps} from '../../interfaces/components/button';
import {Theme} from '../../theme/lightTheme';
import TextGeneric from '../textGeneric';
import Icon from '../icon';
import MyPressable from '../myPressable';

const Container = styled.View<{radius?: number}>`
  justify-content: center;
  flex-direction: row;
  border-radius: ${({radius}) => radius}px;
`;
const ContentContainer = styled(MyPressable)`
  height: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: row;
`;
const Title = styled(TextGeneric)`
  font-size: ${({theme}) => getByScreenSize(theme.text.s7, theme.text.s8)}px;
  color: ${({theme, outline, secondaryRipple}) =>
    outline
      ? secondaryRipple
        ? theme.secondary
        : theme.button.outline
      : theme.button.text};
  padding-horizontal: 2%;
`;
const IconContainer = styled.View`
  position: absolute;
  align-items: center;
  justify-content: center;
`;
const LeftIconContainer = styled(IconContainer)`
  left: 5%;
`;
const RightIconContainer = styled(IconContainer)`
  right: 5%;
`;

const GenericButton: React.FC<GenericButtonProps> = ({
  radius = 10,
  disabled = false,
  outline = false,
  showRightIcon = false,
  style = {},
  isSmall = false,
  isLabelGradient = false,
  dynamicWidth = false,
  dynamicHeight = false,
  label,
  labelStyle,
  onPress,
  rightIconName,
  rightIconColor,
  rightIconSize,
  rightIconStyle,
  rightIconContainerStyle,
  showLeftIcon,
  leftIconName,
  leftIconColor,
  leftIconSize,
  leftIconStyle,
  leftIconContainerStyle,
  secondaryRipple = false,
  backgroundColor,
  outlineBackgroundColor,
  customLeftComponent,
  customRightComponent,
  rightIconStrokeColor,
  ...props
}) => {
  /**
   * Represents the current theme.
   * @type {Theme}
   */
  const theme = useTheme() as Theme;

  const _renderButton = () => {
    return (
      <Container
        radius={radius}
        style={[
          {
            minHeight: isSmall
              ? getByScreenSize(theme.text.s55, theme.text.s55) + 20
              : getByScreenSize(45, 50),
            padding: outline ? 1 : 0,
            backgroundColor: outline
              ? 'transparent'
              : secondaryRipple
                ? theme.secondary
                : theme.primary,
          },
          !dynamicHeight && {
            height: isSmall
              ? getByScreenSize(theme.text.s7, theme.text.s7) + 20
              : getByScreenSize(50, 50),
          },
          !dynamicWidth && {
            width: '100%',
            alignItems: 'center',
          },
          style,
        ]}>
        <ContentContainer
          disableOpacityAnimation={outline}
          primaryRipple={!secondaryRipple}
          secondaryRipple={secondaryRipple}
          style={[
            !dynamicWidth && {
              width: '100%',
            },
          ]}
          containerStyle={[
            {
              backgroundColor: outline
                ? backgroundColor
                  ? theme.text.light
                  : outlineBackgroundColor
                    ? outlineBackgroundColor
                    : theme.homeBackground
                : 'transparent',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'row',
              height: '100%',
            },
            !dynamicWidth && {
              width: '100%',
            },
          ]}
          borderRadius={radius}
          onPress={onPress}
          disabled={disabled}
          {...props}>
          {customLeftComponent && customLeftComponent()}
          {showLeftIcon && !!leftIconName && (
            <LeftIconContainer style={leftIconContainerStyle}>
              <Icon
                name={leftIconName}
                color={
                  leftIconColor
                    ? leftIconColor
                    : outline
                      ? theme.button.outline
                      : theme.button.text
                }
                size={leftIconSize ? leftIconSize : theme.text.s6}
                style={leftIconStyle}
              />
            </LeftIconContainer>
          )}
          <Title
            withGradient={!labelStyle?.color && (outline || isLabelGradient)}
            semiBold
            secondaryRipple={secondaryRipple}
            outline={outline}
            style={labelStyle}>
            {label}
          </Title>
          {customRightComponent && customRightComponent()}
          {showRightIcon && !!rightIconName && (
            <RightIconContainer style={rightIconContainerStyle}>
              <Icon
                name={rightIconName}
                color={
                  rightIconColor
                    ? rightIconColor
                    : outline
                      ? theme.button.outline
                      : theme.button.text
                }
                stroke={rightIconStrokeColor}
                size={rightIconSize ? rightIconSize : theme.text.s6}
                style={rightIconStyle}
              />
            </RightIconContainer>
          )}
        </ContentContainer>
      </Container>
    );
  };
  return _renderButton();
};

export default GenericButton;
